import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { OfflineIdentity } from './offline-identity';

/**
 * Whether this tab is in OFFLINE MODE, and under which (unverified) identity. Dependency-free on
 * purpose: the HTTP interceptor, `CheckAuthService` and `AuthRefreshService` all read it, and each
 * of them sits on the HttpClient / auth graph, where a richer service would close a DI cycle.
 *
 * **Offline mode is NOT a session.** Entering it dispatches nothing: the NgRx `auth` slice stays
 * `EMPTY_AUTH` and `APPLY_SESSION` keeps its single-writer role untouched. Every guard, effect and
 * root store that keys off `isAuthenticated` therefore sees "no session" and does nothing -- no
 * settings load, no push heartbeat, no stats stream, no bearer anywhere -- which is exactly right for
 * a tab that cannot reach the server. Only the two places that must know read this state:
 * `CheckAuthService` (which admits the offline routes) and the interceptor (which refuses every API
 * call but the reconnect probe, without touching the network). See CLAUDE.md, "Offline mode".
 */
@Injectable({ providedIn: 'root' })
export class OfflineModeState {
  private readonly subject = new BehaviorSubject<OfflineIdentity | null>(null);
  private requestedUrl: string | null = null;
  private landed: string | null = null;

  readonly identity$: Observable<OfflineIdentity | null> = this.subject.asObservable();
  readonly active$: Observable<boolean> = this.subject.pipe(
    map((i) => i !== null),
    distinctUntilChanged(),
  );

  get active(): boolean {
    return this.subject.value !== null;
  }

  get identity(): OfflineIdentity | null {
    return this.subject.value;
  }

  /**
   * The deep link the boot asked for when offline mode cannot serve it (the guards send the tab to
   * the offline home instead): `OfflineSessionService` takes it back when the session returns, so a
   * gateway restart or a dead Wi-Fi does not cost the page the user opened. Null when the requested
   * URL was itself an offline route (the tab stays on it) or there was none.
   */
  get returnUrl(): string | null {
    return this.requestedUrl;
  }

  /**
   * Where the tab actually landed once offline mode began: the URL of the first navigation that
   * ended in it, AFTER the router's redirects (`OfflineSessionService` records it). The offline home
   * itself is not that URL: `/app/ingest/scan` redirects to its default child `/app/ingest/scan/scan`
   * (review round 6). Null until the first navigation ends.
   */
  get landedUrl(): string | null {
    return this.landed;
  }

  /** Records {@link landedUrl}: the first call while offline mode lasts wins. */
  markLanded(url: string): void {
    if (this.subject.value !== null && this.landed === null) {
      this.landed = url;
    }
  }

  enter(identity: OfflineIdentity, returnUrl: string | null = null): void {
    this.requestedUrl = returnUrl;
    this.landed = null;
    this.subject.next(identity);
  }

  exit(): void {
    this.requestedUrl = null;
    this.landed = null;
    if (this.subject.value !== null) {
      this.subject.next(null);
    }
  }
}
